import { useState } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { devHubGhecAuthApiRef } from '../../../../apis';
import type {
  ChannelExecutionMode,
  ContextEntry,
  FlowPool,
  Instruction,
} from './types';
import type { EntryCredentialsState } from './EntryCredentialsFields';
import { EMPTY_ENTRY_CREDENTIALS } from './EntryCredentialsFields';
import { computeVaultEntryKey, entryKey } from './utils';

/** Provides authenticated headers for backend calls. */
export function useAuthHeaders() {
  const identityApi = useApi(identityApiRef);
  const githubAuthApi = useApi(devHubGhecAuthApiRef);

  const getAuthHeaders = async (includeGithub = false) => {
    const [{ token }, githubToken] = await Promise.all([
      identityApi.getCredentials(),
      includeGithub
        ? githubAuthApi
            .getAccessToken(undefined, { optional: true })
            .catch(() => undefined)
        : Promise.resolve(undefined),
    ]);
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(githubToken ? { 'X-GitHub-Token': githubToken } : {}),
    };
  };

  return { getAuthHeaders, identityApi };
}

/**
 * Manages app-level instructions state and persistence.
 * @param backendUrl backendUrl
 * @param appId appId
 */
export function useInstructions(backendUrl: string, appId: string | undefined) {
  const { getAuthHeaders } = useAuthHeaders();

  const [instructions, setInstructions] = useState<Instruction[]>([]);
  const [newInstruction, setNewInstruction] = useState('');
  const [saving, setSaving] = useState(false);
  const [rewriting, setRewriting] = useState(false);
  const [rewrittenDraft, setRewrittenDraft] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (updatedInstructions: Instruction[]) => {
    if (!appId) return;
    setSaving(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(
        `${backendUrl}/api/agent/app/${appId}/slack-settings`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ instructions: updatedInstructions }),
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(
          data.error ?? `Failed to save instructions (HTTP ${res.status}).`,
        );
        return;
      }
      setInstructions(updatedInstructions);
      setSaveSuccess(true);
    } catch {
      setError('Failed to save instructions.');
    } finally {
      setSaving(false);
    }
  };

  const add = async () => {
    if (!newInstruction.trim()) return;
    await save([
      ...instructions,
      { text: newInstruction.trim(), active: true },
    ]);
    setNewInstruction('');
  };

  const remove = async (index: number) => {
    await save(instructions.filter((_, i) => i !== index));
  };

  const toggle = async (index: number) => {
    await save(
      instructions.map((inst, i) =>
        i === index ? { ...inst, active: !inst.active } : inst,
      ),
    );
  };

  const rewrite = async () => {
    if (!appId || !newInstruction.trim()) return;
    setRewriting(true);
    setRewrittenDraft(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(
        `${backendUrl}/api/agent/app/${appId}/rewrite-instructions`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ draft: newInstruction }),
        },
      );
      const data = await res.json();
      setRewrittenDraft(data.rewritten ?? null);
    } catch {
      setError('Rewrite failed.');
    } finally {
      setRewriting(false);
    }
  };

  return {
    instructions,
    setInstructions,
    newInstruction,
    setNewInstruction,
    saving,
    rewriting,
    rewrittenDraft,
    setRewrittenDraft,
    saveSuccess,
    setSaveSuccess,
    error,
    setError,
    add,
    remove,
    toggle,
    rewrite,
  };
}

/**
 * Manages channel context entries state and persistence.
 * @param backendUrl backendUrl
 */
export function useChannelEntries(backendUrl: string) {
  const { getAuthHeaders, identityApi } = useAuthHeaders();

  const [channelEntries, setChannelEntries] = useState<ContextEntry[]>([]);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [refreshingGithubKey, setRefreshingGithubKey] = useState<string | null>(
    null,
  );
  const [queuedGithubKey, setQueuedGithubKey] = useState<string | null>(null);
  const [queuedGithubRunId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async (channelId: string) => {
    setLoadingEntries(true);
    setChannelEntries([]);
    try {
      const { token } = await identityApi.getCredentials();
      const res = await fetch(
        `${backendUrl}/api/agent/channel/${channelId}/context-entries`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      setChannelEntries(data.entries ?? []);
    } catch {
      setError(`Failed to load context for ${channelId}.`);
    } finally {
      setLoadingEntries(false);
    }
  };

  const save = async (
    channelId: string,
    next: ContextEntry[],
    force?: boolean,
    applicationId?: number,
  ) => {
    const headers = await getAuthHeaders(true);
    const res = await fetch(
      `${backendUrl}/api/agent/channel/${channelId}/context-entries`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({
          entries: next,
          ...(force && { force }),
          ...(applicationId !== undefined && { applicationId }),
        }),
      },
    );
    if (!res.ok) throw new Error('Failed to save');
    setChannelEntries(next);
  };

  const removeEntry = async (
    channelId: string,
    index: number,
    applicationId?: number,
  ) => {
    const entry = channelEntries[index];
    try {
      if (entry.type === 'github') {
        const sourceRef = `${entry.org}/${entry.repo}/${entry.path}`;
        const { token } = await identityApi.getCredentials();
        const res = await fetch(
          `${backendUrl}/api/agent/channel/${channelId}/context-entries`,
          {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              sourceRef,
              ...(applicationId !== undefined && { applicationId }),
            }),
          },
        );
        if (!res.ok) throw new Error('Failed to remove');
        setChannelEntries(channelEntries.filter((_, i) => i !== index));
      } else {
        await save(
          channelId,
          channelEntries.filter((_, i) => i !== index),
          undefined,
          applicationId,
        );
      }
    } catch {
      setError('Failed to remove context entry.');
    }
  };

  const toggleEntry = async (
    channelId: string,
    index: number,
    applicationId?: number,
  ) => {
    const updated = channelEntries.map((entry, i) =>
      i === index ? { ...entry, active: !(entry.active !== false) } : entry,
    );
    try {
      await save(channelId, updated, undefined, applicationId);
    } catch {
      setError('Failed to toggle context entry.');
    }
  };

  const refreshGithubEntry = async (
    channelId: string,
    entry: Extract<ContextEntry, { type: 'github' }>,
    applicationId?: number,
  ) => {
    const refreshKey = `${entry.org}/${entry.repo}`;
    setRefreshingGithubKey(refreshKey);
    setError(null);
    try {
      const headers = await getAuthHeaders(true);
      const res = await fetch(
        `${backendUrl}/api/agent/channel/${channelId}/context-entries/refresh`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            org: entry.org,
            repo: entry.repo,
            path: entry.path,
            ...(applicationId !== undefined && { applicationId }),
          }),
        },
      );
      if (!res.ok) throw new Error('Failed to refresh');
      setQueuedGithubKey(refreshKey);
      setTimeout(() => {
        setQueuedGithubKey(prev => (prev === refreshKey ? null : prev));
      }, 5000);
    } catch {
      setError(`Failed to refresh ${entry.org}/${entry.repo}.`);
    } finally {
      setRefreshingGithubKey(null);
    }
  };

  return {
    channelEntries,
    setChannelEntries,
    loadingEntries,
    refreshingGithubKey,
    queuedGithubKey,
    queuedGithubRunId,
    error,
    setError,
    load,
    save,
    removeEntry,
    toggleEntry,
    refreshGithubEntry,
  };
}

/**
 * Manages channel credentials state and persistence.
 * @param backendUrl backendUrl
 */
export function useCredentials(backendUrl: string) {
  const { identityApi } = useAuthHeaders();

  const [hasCredentials, setHasCredentials] = useState<boolean | null>(null);
  const [credTokenUrl, setCredTokenUrl] = useState('');
  const [credClientId, setCredClientId] = useState('');
  const [credClientSecret, setCredClientSecret] = useState('');
  const [credScopes, setCredScopes] = useState('');
  const [credSaving, setCredSaving] = useState(false);
  const [credRemoving, setCredRemoving] = useState(false);
  const [credError, setCredError] = useState<string | null>(null);
  const [credExpanded, setCredExpanded] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
  const [credTokenUrlError, setCredTokenUrlError] = useState<string | null>(
    null,
  );

  const loadStatus = async (channelId: string) => {
    setHasCredentials(null);
    setCredTokenUrl('');
    setCredClientId('');
    setCredClientSecret('');
    setCredScopes('');
    setCredError(null);
    try {
      const { token } = await identityApi.getCredentials();
      const res = await fetch(
        `${backendUrl}/api/agent/channel/${channelId}/credentials/status`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) {
        setCredError(
          res.status === 503
            ? 'Vault is unavailable — credential status unknown.'
            : `Failed to load credential status (HTTP ${res.status}).`,
        );
        setHasCredentials(false);
        return;
      }
      const data = await res.json();
      setHasCredentials(data.hasCredentials ?? false);
    } catch (err) {
      setCredError(
        `Failed to load credential status: ${err instanceof Error ? err.message : String(err)}`,
      );
      setHasCredentials(false);
    }
  };

  const saveCredential = async (channelId: string) => {
    if (
      !credTokenUrl.trim() ||
      !credClientId.trim() ||
      !credClientSecret.trim()
    )
      return;
    try {
      const _url = new URL(credTokenUrl.trim());
      void _url;
    } catch {
      setCredTokenUrlError(
        'Enter a valid URL (e.g. https://sat.example.com/oauth/token)',
      );
      return;
    }
    setCredTokenUrlError(null);
    setCredSaving(true);
    setCredError(null);
    try {
      const { token } = await identityApi.getCredentials();
      const res = await fetch(
        `${backendUrl}/api/agent/channel/${channelId}/credentials`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            tokenUrl: credTokenUrl.trim(),
            clientId: credClientId.trim(),
            clientSecret: credClientSecret.trim(),
            scopes: credScopes.trim(),
          }),
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setCredError(data.error ?? 'Failed to save credentials.');
        return;
      }
      setCredTokenUrl('');
      setCredClientId('');
      setCredClientSecret('');
      setCredScopes('');
      setHasCredentials(true);
    } catch {
      setCredError('Failed to save credentials.');
    } finally {
      setCredSaving(false);
    }
  };

  const removeCredential = async (channelId: string) => {
    setConfirmRemoveOpen(false);
    setCredRemoving(true);
    setCredError(null);
    try {
      const { token } = await identityApi.getCredentials();
      const res = await fetch(
        `${backendUrl}/api/agent/channel/${channelId}/credentials`,
        {
          method: 'DELETE',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setCredError(data.error ?? 'Failed to remove credentials.');
        return;
      }
      setHasCredentials(false);
    } catch {
      setCredError('Failed to remove credentials.');
    } finally {
      setCredRemoving(false);
    }
  };

  return {
    hasCredentials,
    credTokenUrl,
    setCredTokenUrl,
    credClientId,
    setCredClientId,
    credClientSecret,
    setCredClientSecret,
    credScopes,
    setCredScopes,
    credSaving,
    credRemoving,
    credError,
    setCredError,
    credExpanded,
    setCredExpanded,
    confirmRemoveOpen,
    setConfirmRemoveOpen,
    credTokenUrlError,
    setCredTokenUrlError,
    loadStatus,
    saveCredential,
    removeCredential,
  };
}

/**
 * Manages FLOW:Context pool picker state.
 * @param backendUrl backendUrl
 */
export function useFlowPools(backendUrl: string) {
  const { identityApi } = useAuthHeaders();

  const [flowPickerOpen, setFlowPickerOpen] = useState(false);
  const [flowPools, setFlowPools] = useState<FlowPool[]>([]);
  const [flowPoolsLoading, setFlowPoolsLoading] = useState(false);
  const [flowPoolsError, setFlowPoolsError] = useState<string | null>(null);
  const [selectedPool, setSelectedPool] = useState<FlowPool | null>(null);
  const [flowSaving, setFlowSaving] = useState(false);

  const open = async () => {
    setFlowPickerOpen(true);
    setSelectedPool(null);
    setFlowPoolsError(null);
    setFlowPoolsLoading(true);
    try {
      const { token } = await identityApi.getCredentials();
      const res = await fetch(`${backendUrl}/api/agent/flow-context/pools`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setFlowPools(data.pools ?? []);
    } catch {
      setFlowPoolsError('Failed to load FLOW:Context pools.');
    } finally {
      setFlowPoolsLoading(false);
    }
  };

  const linkPool = async (
    channelId: string,
    channelEntries: ContextEntry[],
    saveChannelEntries: (entries: ContextEntry[]) => Promise<void>,
    credentials?: EntryCredentialsState,
    applicationId?: number,
  ) => {
    if (!selectedPool) return;
    const flowEntry: ContextEntry = {
      type: 'flow',
      org: selectedPool.org,
      poolId: selectedPool.poolId,
    };
    if (channelEntries.some(e => entryKey(e) === entryKey(flowEntry))) {
      setFlowPickerOpen(false);
      return;
    }
    setFlowSaving(true);
    setFlowPoolsError(null);
    try {
      const { token } = await identityApi.getCredentials();
      const verifyRes = await fetch(
        `${backendUrl}/api/agent/flow-context/verify-pool`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            org: selectedPool.org,
            poolId: selectedPool.poolId,
            ...(credentials?.tokenUrl &&
            credentials?.clientId &&
            credentials?.clientSecret
              ? {
                  tokenUrl: credentials.tokenUrl,
                  clientId: credentials.clientId,
                  clientSecret: credentials.clientSecret,
                  scopes: credentials.scopes,
                  authType: credentials.authType,
                }
              : {}),
          }),
        },
      );
      const verifyData = await verifyRes.json();
      if (!verifyData.valid) {
        setFlowPoolsError(verifyData.error ?? 'Pool not found.');
        return;
      }
      await saveChannelEntries([...channelEntries, flowEntry]);

      // Save optional per-entry credentials to Vault after the pool is linked
      if (credentials && channelId) {
        try {
          const vaultKey = await computeVaultEntryKey({
            type: 'flow',
            org: selectedPool.org,
            poolId: selectedPool.poolId,
          });
          await fetch(
            `${backendUrl}/api/agent/channel/${channelId}/context-entry-credentials`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({
                entryKey: vaultKey,
                ...credentials,
                ...(applicationId !== undefined ? { applicationId } : {}),
              }),
            },
          );
        } catch {
          // Non-blocking — credentials can be set later via the edit button
        }
      }

      setFlowPickerOpen(false);
    } catch {
      setFlowPoolsError('Failed to verify or save pool.');
    } finally {
      setFlowSaving(false);
    }
  };

  return {
    flowPickerOpen,
    setFlowPickerOpen,
    flowPools,
    flowPoolsLoading,
    flowPoolsError,
    selectedPool,
    setSelectedPool,
    flowSaving,
    open,
    linkPool,
  };
}

/**
 * Manages MCP server picker state and entry persistence.
 * @param backendUrl backendUrl
 */
export function useMcpServer(backendUrl: string) {
  const { identityApi } = useAuthHeaders();

  const [mcpPickerOpen, setMcpPickerOpen] = useState(false);
  const [mcpSaving, setMcpSaving] = useState(false);
  const [mcpError, setMcpError] = useState<string | null>(null);

  const open = () => {
    setMcpPickerOpen(true);
    setMcpError(null);
  };

  const addServer = async (
    channelId: string,
    channelEntries: ContextEntry[],
    saveChannelEntries: (entries: ContextEntry[]) => Promise<void>,
    url: string,
    label: string | undefined,
    credentials?: EntryCredentialsState,
    applicationId?: number,
  ) => {
    const mcpEntry: ContextEntry = {
      type: 'mcp',
      url,
      auth: 'azure-ad',
      ...(label ? { label } : {}),
    };
    if (channelEntries.some(e => entryKey(e) === entryKey(mcpEntry))) {
      setMcpPickerOpen(false);
      return;
    }
    setMcpSaving(true);
    setMcpError(null);
    try {
      await saveChannelEntries([...channelEntries, mcpEntry]);
      if (
        credentials?.tokenUrl &&
        credentials?.clientId &&
        credentials?.clientSecret &&
        channelId
      ) {
        try {
          const { token } = await identityApi.getCredentials();
          const vaultKey = await computeVaultEntryKey({ type: 'mcp', url });
          await fetch(
            `${backendUrl}/api/agent/channel/${channelId}/context-entry-credentials`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
              body: JSON.stringify({
                entryKey: vaultKey,
                ...credentials,
                ...(applicationId !== undefined ? { applicationId } : {}),
              }),
            },
          );
        } catch {
          // Non-blocking — credentials can be set later via the edit button
        }
      }
      setMcpPickerOpen(false);
    } catch {
      setMcpError('Failed to save MCP server entry.');
    } finally {
      setMcpSaving(false);
    }
  };

  return {
    mcpPickerOpen,
    setMcpPickerOpen,
    mcpSaving,
    mcpError,
    open,
    addServer,
  };
}

/**
 * Manages channel context admin list state and persistence.
 * @param backendUrl backendUrl
 */
export function useContextAdmins(backendUrl: string) {
  const { getAuthHeaders, identityApi } = useAuthHeaders();

  const [admins, setAdmins] = useState<string[]>([]);
  const [loadingAdmins, setLoadingAdmins] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [adminSaving, setAdminSaving] = useState(false);
  const [adminRemoving, setAdminRemoving] = useState(false);
  const [adminError, setAdminError] = useState<string | null>(null);

  const load = async (channelId: string) => {
    setLoadingAdmins(true);
    setAdmins([]);
    setAdminError(null);
    try {
      const { token } = await identityApi.getCredentials();
      const res = await fetch(
        `${backendUrl}/api/agent/channel/${channelId}/context-admins`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      setAdmins(data.admins ?? []);
    } catch {
      setAdminError(`Failed to load context admins for channel.`);
    } finally {
      setLoadingAdmins(false);
    }
  };

  const addAdmin = async (
    channelId: string,
    email: string,
    applicationId?: number,
  ): Promise<boolean> => {
    if (!email.trim()) return false;
    setAdminSaving(true);
    setAdminError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(
        `${backendUrl}/api/agent/channel/${channelId}/context-admins`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            email: email.trim(),
            ...(applicationId !== undefined && { applicationId }),
          }),
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setAdminError(data.error ?? 'Failed to add admin.');
        return false;
      }
      const data = await res.json();
      setAdmins(data.admins ?? []);
      setNewAdminEmail('');
      return true;
    } catch {
      setAdminError('Failed to add admin.');
      return false;
    } finally {
      setAdminSaving(false);
    }
  };

  const removeAdmin = async (
    channelId: string,
    email: string,
    applicationId?: number,
  ) => {
    setAdminRemoving(true);
    setAdminError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(
        `${backendUrl}/api/agent/channel/${channelId}/context-admins/${encodeURIComponent(email)}`,
        {
          method: 'DELETE',
          headers,
          body:
            applicationId !== undefined
              ? JSON.stringify({ applicationId })
              : undefined,
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setAdminError(data.error ?? 'Failed to remove admin.');
        return;
      }
      const data = await res.json();
      setAdmins(data.admins ?? []);
    } catch {
      setAdminError('Failed to remove admin.');
    } finally {
      setAdminRemoving(false);
    }
  };

  return {
    admins,
    setAdmins,
    loadingAdmins,
    newAdminEmail,
    setNewAdminEmail,
    adminSaving,
    adminRemoving,
    adminError,
    setAdminError,
    load,
    addAdmin,
    removeAdmin,
  };
}

/**
 * Manages per-entry Vault credentials for a2a agent and FLOW:Context entries.
 * @param backendUrl backendUrl
 */
export function useEntryCredentials(backendUrl: string) {
  const { identityApi } = useAuthHeaders();

  const [hasCredentials, setHasCredentials] = useState<boolean | null>(null);
  const [creds, setCreds] = useState<EntryCredentialsState>(
    EMPTY_ENTRY_CREDENTIALS,
  );
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenUrlError, setTokenUrlError] = useState<string | null>(null);

  const reset = () => {
    setCreds(EMPTY_ENTRY_CREDENTIALS);
    setError(null);
    setTokenUrlError(null);
    setHasCredentials(null);
  };

  const loadStatus = async (channelId: string, vaultEntryKey: string) => {
    setHasCredentials(null);
    setError(null);
    try {
      const { token } = await identityApi.getCredentials();
      const res = await fetch(
        `${backendUrl}/api/agent/channel/${channelId}/context-entry-credentials/status?entryKey=${encodeURIComponent(vaultEntryKey)}`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) {
        setHasCredentials(false);
        return;
      }
      const data = await res.json();
      setHasCredentials(data.hasCredentials ?? false);
    } catch {
      setHasCredentials(false);
    }
  };

  const saveCredential = async (
    channelId: string,
    vaultEntryKey: string,
    applicationId?: number,
  ) => {
    if (
      !creds.tokenUrl.trim() ||
      !creds.clientId.trim() ||
      !creds.clientSecret.trim()
    )
      return;
    try {
      const _u = new URL(creds.tokenUrl.trim());
      void _u;
    } catch {
      setTokenUrlError(
        'Enter a valid URL (e.g. https://sat.example.com/oauth/token)',
      );
      return;
    }
    setTokenUrlError(null);
    setSaving(true);
    setError(null);
    try {
      const { token } = await identityApi.getCredentials();
      const res = await fetch(
        `${backendUrl}/api/agent/channel/${channelId}/context-entry-credentials`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            entryKey: vaultEntryKey,
            tokenUrl: creds.tokenUrl.trim(),
            clientId: creds.clientId.trim(),
            clientSecret: creds.clientSecret.trim(),
            scopes: creds.scopes.trim(),
            authType: creds.authType,
            ...(applicationId !== undefined ? { applicationId } : {}),
          }),
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error ?? 'Failed to save credentials.');
        return;
      }
      setCreds(EMPTY_ENTRY_CREDENTIALS);
      setHasCredentials(true);
    } catch {
      setError('Failed to save credentials.');
    } finally {
      setSaving(false);
    }
  };

  const removeCredential = async (
    channelId: string,
    vaultEntryKey: string,
    applicationId?: number,
  ) => {
    setRemoving(true);
    setError(null);
    try {
      const { token } = await identityApi.getCredentials();
      const res = await fetch(
        `${backendUrl}/api/agent/channel/${channelId}/context-entry-credentials`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            entryKey: vaultEntryKey,
            ...(applicationId !== undefined ? { applicationId } : {}),
          }),
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error ?? 'Failed to remove credentials.');
        return;
      }
      setHasCredentials(false);
    } catch {
      setError('Failed to remove credentials.');
    } finally {
      setRemoving(false);
    }
  };

  return {
    hasCredentials,
    creds,
    setCreds,
    saving,
    removing,
    error,
    setError,
    tokenUrlError,
    reset,
    loadStatus,
    saveCredential,
    removeCredential,
  };
}

/**
 * Manages per-channel execution policy (default vs channel-only mode).
 * @param backendUrl backendUrl
 */
export function useChannelPolicy(backendUrl: string) {
  const { getAuthHeaders, identityApi } = useAuthHeaders();

  const [mode, setMode] = useState<ChannelExecutionMode>('default');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async (channelId: string) => {
    setLoading(true);
    setError(null);
    try {
      const { token } = await identityApi.getCredentials();
      const res = await fetch(
        `${backendUrl}/api/agent/channel/${channelId}/context-policy`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} },
      );
      if (!res.ok) throw new Error();
      const data = (await res.json()) as {
        mode?: ChannelExecutionMode;
      };
      setMode(data.mode === 'channel_only' ? 'channel_only' : 'default');
    } catch {
      setMode('default');
      setError('Failed to load channel policy.');
    } finally {
      setLoading(false);
    }
  };

  const save = async (
    channelId: string,
    nextMode: ChannelExecutionMode,
    applicationId?: number,
  ) => {
    setSaving(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(
        `${backendUrl}/api/agent/channel/${channelId}/context-policy`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            mode: nextMode,
            ...(applicationId !== undefined && { applicationId }),
          }),
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error ?? 'Failed to save channel policy.');
        return false;
      }
      setMode(nextMode);
      return true;
    } catch {
      setError('Failed to save channel policy.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    mode,
    setMode,
    loading,
    saving,
    error,
    setError,
    load,
    save,
  };
}
